/**
 * Look up a combat-log position on the platform (planet, world and zone maps,
 * pixel positions), debounced for as-you-type input.
 *
 * Idle (no request) until the area and x/y are all set. A newer input aborts the
 * request in flight, so a slow answer never overwrites a newer one. api-server
 * caches results, so re-opening a screenshot is cheap.
 */

import { useEffect, useState } from 'react';
import type { PlatformLocateResult } from '@swtorbit/shared-socket-events';
import { locatePlatform } from '../utils/platformApi';

export interface UsePlatformLocate {
  result: PlatformLocateResult | null;
  loading: boolean;
  error: string | null;
}

export function usePlatformLocate(
  logAreaId: number | null,
  x: number | null,
  y: number | null,
  z: number | null,
  delayMs = 400,
): UsePlatformLocate {
  const [result, setResult] = useState<PlatformLocateResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (logAreaId === null || x === null || y === null) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      locatePlatform(logAreaId, x, y, z ?? undefined, controller.signal)
        .then((r) => { setResult(r); setError(null); })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return;
          setResult(null);
          setError(e instanceof Error ? e.message : 'Failed to locate position');
        })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, delayMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [logAreaId, x, y, z, delayMs]);

  return { result, loading, error };
}
